import { useCallback, useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import HelpButton from 'Components/HelpButton';

import CreateReview from 'Pages/TeamDashboard/CreateReview';

import { closeCreateReviewModal, openCreateReviewModal } from 'Redux/Actions/createReviewActions';
import { getReviewDataAction } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';
import { fetchSubscriptionInfo } from 'Redux/Actions/proActions';
import { setUserProfile } from 'Redux/Actions/userActions';
import actionTypes from 'Redux/constants';

import { ROUTES } from 'routes/route.constants';

import { getSingleDesignReview } from 'services/teamDashboardReviews';

import { errorReactToastify } from 'utlis/toasts';

import DashboardHeader from './components/DashboardHeader';
import LowCreditsAlert from './components/LowCreditsAlert';
import Overview from './components/Overview';
import ReviewsSection from './components/ReviewsSection';
import Sidebar from './components/Sidebar';

import styles from './styles.module.scss';

const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationObject = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [isLoadingReview, setIsLoadingReview] = useState(false);

  const authUser = useSelector(state => state.userStore);
  const reviewCount = useSelector(state => state.designReview.reviewCount);
  const { isOpen: isCreateReviewOpen, selectedReview } = useSelector(state => state.createReview);
  const [previousReviewsRemaining, setPreviousReviewsRemaining] = useState(null);
  const [showLowCreditsAlert, setShowLowCreditsAlert] = useState(false);

  // Handle opening review popup for existing review
  const handleViewReview = review => {
    dispatch(openCreateReviewModal(review));
    dispatch(setCurrentRecord(review));
  };

  // Handle closing review popup
  const handleCloseReview = () => {
    dispatch(closeCreateReviewModal());
  };

  // Handle opening new review popup
  const handleCreateNewReview = useCallback(() => {
    dispatch(openCreateReviewModal(null));
  }, [dispatch]);

  // Fetch review by UUID and open it
  const fetchAndOpenReview = useCallback(async reviewId => {
    if (!reviewId) {
      return;
    }

    setIsLoadingReview(true);

    try {
      const reviewData = await getSingleDesignReview(reviewId);

      if (reviewData && typeof reviewData === 'object' && reviewData.uuid) {
        dispatch(openCreateReviewModal(reviewData));

        // Clean up URL parameters
        setSearchParams({});
      }
      else {
        setSearchParams({});
      }
    }
    catch (error) {
      // Clear URL parameters even on error
      setSearchParams({});
      errorReactToastify(
        'This design review does not exist or you don\'t have permission to view it.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '530px' },
        }
      );
    }
    finally {
      setIsLoadingReview(false);
    }
  }, [setSearchParams]);

  const checkAuthentication = async () => {
    try {
      const userObjectString = localStorage.getItem('userObject');

      if (!userObjectString) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

        navigate(loginUrl);

        return;
      }

      const userObject = JSON.parse(userObjectString);

      if (!userObject || !userObject.uuid) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

        navigate(loginUrl);

        return;
      }

      // Dispatch userObject to Redux to populate userStore
      dispatch({
        type: actionTypes.SIGN_IN_USER,
        payload: userObject,
      });
      setIsCheckingAuth(false);
    }
    catch (error) {
      // Handle invalid JSON in localStorage
      // eslint-disable-next-line no-console
      console.error('Error parsing userObject from localStorage:', error);
      localStorage.removeItem('userObject');

      const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

      navigate(loginUrl);
    }
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (authUser && authUser.uuid) {
      dispatch(setUserProfile(authUser));
      // Fetch review data when user is authenticated
      dispatch(getReviewDataAction());
      // Fetch subscription info to determine user's tier
      dispatch(fetchSubscriptionInfo());
    }
  }, [authUser, dispatch]);

  // Check localStorage for modal state on component mount (only after auth check)
  useEffect(() => {
    // Wait for auth check to complete and ensure no URL parameters are being processed
    if (isCheckingAuth) {
      return;
    }

    const shouldReopenModal = localStorage.getItem('designReview_shouldReopenModal') === 'true';
    const hasUrlParams = searchParams.get('createReview') && searchParams.get('openReview');

    if (shouldReopenModal && !hasUrlParams) {
      handleCreateNewReview();

      // Clear the localStorage flags after opening modal
      localStorage.removeItem('designReview_shouldReopenModal');
    }
  }, [handleCreateNewReview, isCheckingAuth, searchParams]);

  // Handle URL parameters on component mount
  useEffect(() => {
    const createReviewParam = searchParams.get('createReview');
    const openReviewParam = searchParams.get('openReview');

    if (createReviewParam === 'true' && openReviewParam) {
      // Wait for auth check to complete before fetching review
      if (!isCheckingAuth) {
        fetchAndOpenReview(openReviewParam);
      }
    }
  }, [searchParams, isCheckingAuth, fetchAndOpenReview]);

  // Check if user has no credits and show alert
  useEffect(() => {
    if (reviewCount && reviewCount.reviews_remaining !== undefined) {
      const currentRemaining = reviewCount.reviews_remaining;

      // Show alert if user has 0 credits, either:
      // 1. Just ran out (had credits before, now has 0)
      // 2. Already has 0 credits when loading the page (first time check)
      if (currentRemaining === 0) {
        if (
          // Case 1: Just ran out of credits
          (previousReviewsRemaining !== null && previousReviewsRemaining > 0)
          // Case 2: First time loading and already has 0 credits
          || (previousReviewsRemaining === null)
        ) {
          setShowLowCreditsAlert(true);
        }
      }

      // Update previous value for next comparison
      setPreviousReviewsRemaining(currentRemaining);
    }
  }, [reviewCount?.reviews_remaining, previousReviewsRemaining]);

  const handleCloseLowCreditsAlert = () => {
    setShowLowCreditsAlert(false);
  };

  // Show loader while checking authentication or loading review data
  if (isCheckingAuth || isLoadingReview) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner frontColor={'#28BDAE'} />
      </div>
    );
  }

  return (
    <div className={styles['dashboard-container']}>
      <div className="min-h-screen bg-gray-50 grid gap-0 flex w-full">
        <Sidebar />
        <div className="flex flex-1 bg-gray-50 p-2 pl-0 max-h-screen">
          <div className="flex flex-1 flex-col overflow-auto rounded-lg bg-white shadow-figma-sm relative">
            <DashboardHeader />

            <div className="flex flex-1 overflow-auto flex-col px-6 py-6">
              <Overview
                onStatusFilter={setStatusFilter}
                statusFilter={statusFilter}
              />

              <ReviewsSection
                isCreateReviewOpen={isCreateReviewOpen}
                statusFilter={statusFilter}
                onClearStatusFilter={() => setStatusFilter(null)}
                onViewReview={handleViewReview}
                onCreateReviewToggle={handleCreateNewReview}
              />
            </div>

            <HelpButton />
          </div>
        </div>
      </div>

      {/* CreateReview Modal */}
      {isCreateReviewOpen && (
        <div className="fixed inset-0 z-50">
          <CreateReview
            onClose={handleCloseReview}
            existingReviewData={selectedReview}
          />
        </div>
      )}

      {/* Low Credits Alert */}
      {showLowCreditsAlert && (
        <LowCreditsAlert onClose={handleCloseLowCreditsAlert} />
      )}
    </div>
  );
};

export default DashboardHome;
